import { useState, useEffect } from 'react'

interface OfficeState {
  /** Whether Office.js has been initialized */
  isReady: boolean
  /** The Office host app (Word, Excel, etc.) */
  hostType: Office.HostType | null
  /** Whether we're running inside Word specifically */
  isWord: boolean
  /** Error during initialization */
  error: string | null
}

/**
 * Hook for Office.js initialization state.
 *
 * Office.onReady() is called once in main.tsx before React renders.
 * This hook reads the cached result for components that need to check
 * host type or readiness.
 */

// Module-level cache — set once by initOffice()
let officeReady = false
let officeHost: Office.HostType | null = null
let officeError: string | null = null
const listeners = new Set<() => void>()

/** Called once from main.tsx to initialize Office.js */
export async function initOffice(): Promise<void> {
  try {
    // Race Office.onReady() against a 10s timeout to prevent permanent blank screen
    const info = await Promise.race([
      Office.onReady(),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Office.onReady() timed out after 10s')), 10000),
      ),
    ])
    officeReady = true
    officeHost = info.host
    officeError = null
  } catch (err) {
    officeReady = true // Still "ready" — but in error state
    officeError = err instanceof Error ? err.message : 'Office initialization failed'
  }
  // Notify all hooks
  listeners.forEach((fn) => fn())
}

export function useOffice(): OfficeState {
  const [, forceRender] = useState(0)

  useEffect(() => {
    const listener = () => forceRender((n) => n + 1)
    listeners.add(listener)
    return () => {
      listeners.delete(listener)
    }
  }, [])

  return {
    isReady: officeReady,
    hostType: officeHost,
    // Safe check — Office global may not exist yet before script loads
    isWord: officeHost != null && typeof Office !== 'undefined' && officeHost === Office.HostType.Word,
    error: officeError,
  }
}
